import { useState } from "react";
import { useSelector } from "react-redux";
import {
  ArrowCounterClockwise,
  ArrowSquareOut,
  ArrowsClockwise,
  Copy,
  LinkSimple,
  PaperPlaneTilt,
  Prohibit,
  Receipt,
} from "@phosphor-icons/react";
import { selectClinicId, selectIsAdmin } from "../../store/slices/authSlice";
import {
  useCancelGrowPaymentLinkMutation,
  useGetGrowPaymentLinkStatusMutation,
  useSendGrowPaymentLinkMutation,
} from "../../store/services/growApi";
import { useToast } from "../../contexts/ToastContext";
import { formatDateTime } from "../../utils/dateUtils";
import ModalCloseButton from "../ui/ModalCloseButton";
import LoaderLoop from "../shared/loader/LoaderLoop";
import GrowLinkStatusChip from "./GrowLinkStatusChip";
import { GROW_ACTIVE_LINK_STATUSES } from "../../firebase";
import { growMethodLabel } from "./growUi";
import "./GrowIntegration.css";
import "./GrowLinks.css";

const fmtMoney = (n) => `₪${Number(n || 0).toLocaleString("he-IL", { maximumFractionDigits: 2 })}`;

export const copyGrowLink = async (url) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch (_) {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = url;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (error) {
    console.error("[CK-3873] Copy Grow link failed", { errorCode: "CK-3873", errorMessage: error.message, timestamp: new Date().toISOString() });
    return false;
  }
};

/**
 * GrowLinkPopover — details + actions for one payment link. Rendered as a
 * small centered card (bottom sheet on mobile) so it works everywhere a chip
 * can appear (kanban card, list row, appointment modal, patient tab).
 *
 * Props: link (growPaymentLinks doc), isOpen, onClose, allowResend? (Phase 6 flips to true)
 * Error codes: CK-3870 (cancel), CK-3874 (refresh), CK-3875 (resend)
 */
export default function GrowLinkPopover({ link, isOpen, onClose, allowResend = true }) {
  const clinicId = useSelector(selectClinicId);
  const isAdmin = useSelector(selectIsAdmin);
  const { showSuccess, showError, showInfo } = useToast();
  const [cancelLink, { isLoading: cancelling }] = useCancelGrowPaymentLinkMutation();
  const [refreshStatus, { isLoading: refreshing }] = useGetGrowPaymentLinkStatusMutation();
  const [resend, { isLoading: resending }] = useSendGrowPaymentLinkMutation();
  const [confirmCancel, setConfirmCancel] = useState(false);

  if (!isOpen || !link) return null;
  const active = GROW_ACTIVE_LINK_STATUSES.includes(link.status);
  const pay = link.payment || {};
  const busy = cancelling || refreshing || resending;

  const onCopy = async () => {
    if (!link.url) return;
    const ok = await copyGrowLink(link.url);
    if (ok) showSuccess("הקישור הועתק");
    else showError("לא ניתן להעתיק את הקישור");
  };

  const onCancel = async () => {
    const res = await cancelLink({ clinicId, linkId: link.id });
    if (res.error) {
      console.error("[CK-3870] Cancel Grow link failed", { errorCode: "CK-3870", errorMessage: res.error.message, linkId: link.id, timestamp: new Date().toISOString() });
      showError(res.error.message);
      return;
    }
    showSuccess(res.data?.pendingDeactivation ? "הקישור בוטל במערכת; הביטול ב-Grow יושלם בהמשך" : "דרישת התשלום בוטלה");
    setConfirmCancel(false);
    onClose();
  };

  const onRefresh = async () => {
    const res = await refreshStatus({ clinicId, linkId: link.id });
    if (res.error) {
      console.error("[CK-3874] Refresh Grow link status failed", { errorCode: "CK-3874", errorMessage: res.error.message, linkId: link.id, timestamp: new Date().toISOString() });
      showError(res.error.message);
      return;
    }
    if (res.data?.reconciled === "applied") showSuccess("התשלום אותר ב-Grow ועודכן");
    else if (res.data?.grow?.paid) showInfo("Grow מדווחת שהקישור שולם");
    else showInfo(res.data?.grow?.isActive === false ? "הקישור אינו פעיל ב-Grow" : "הקישור עדיין ממתין לתשלום");
  };

  const onResend = async () => {
    const res = await resend({ clinicId, linkId: link.id });
    if (res.error) {
      console.error("[CK-3875] Resend Grow link failed", { errorCode: "CK-3875", errorMessage: res.error.message, linkId: link.id, timestamp: new Date().toISOString() });
      showError(res.error.message);
      return;
    }
    showSuccess(res.data?.channel === "whatsapp" ? "דרישת התשלום נשלחה בוואטסאפ" : res.data?.channel === "sms" ? "דרישת התשלום נשלחה ב-SMS" : "דרישת התשלום נשלחה");
  };

  return (
    <div className="grow-overlay" onClick={onClose} role="presentation">
      <div className="grow-modal grow-modal-sm grow-lpop" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="grow-lpop-title">
        <div className="grow-modal-header">
          <div className="grow-modal-title">
            <span className="grow-modal-icon"><LinkSimple size={20} weight="fill" /></span>
            <div>
              <span className="grow-eyebrow">דרישת תשלום Grow</span>
              <h3 id="grow-lpop-title">{fmtMoney(link.sum)} · {link.patientName || "מטופל"}</h3>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="grow-modal-body">
          <div className="grow-lpop-status">
            <GrowLinkStatusChip link={link} />
            {link.environment === "sandbox" && <span className="grow-badge sandbox">סביבת בדיקות</span>}
          </div>

          <dl className="grow-info-grid grow-lpop-grid">
            <div><dt>נוצר</dt><dd>{link.createdAt ? formatDateTime(link.createdAt) : "—"}{link.createdByName ? ` · ${link.createdByName}` : ""}</dd></div>
            <div><dt>נשלח</dt><dd>{link.sentAt ? `${formatDateTime(link.sentAt)}${link.sentVia ? ` · ${link.sentVia === "whatsapp" ? "וואטסאפ" : link.sentVia === "sms" ? "SMS" : link.sentVia}` : ""}` : "עדיין לא נשלח"}</dd></div>
            <div><dt>תוקף</dt><dd>{link.expiresAt ? formatDateTime(link.expiresAt) : "—"}</dd></div>
            <div><dt>תשלומים מקושרים</dt><dd>{(link.transactionIds || []).length}</dd></div>
            {link.status === "paid" && (
              <>
                <div><dt>שולם</dt><dd>{link.paidAt ? formatDateTime(link.paidAt) : "—"}</dd></div>
                <div><dt>אמצעי</dt><dd>{pay.methodLabel || growMethodLabel(pay.transactionTypeId)}{pay.cardBrand ? ` · ${pay.cardBrand}` : ""}{pay.cardSuffix ? ` ****${pay.cardSuffix}` : ""}</dd></div>
                <div><dt>אסמכתא</dt><dd dir="ltr">{pay.asmachta || "—"}</dd></div>
                <div><dt>משלם</dt><dd>{pay.payerName || "—"}</dd></div>
              </>
            )}
            {link.status === "failed" && link.growErrorId != null && (
              <div><dt>שגיאת Grow</dt><dd dir="ltr">{String(link.growErrorId)}</dd></div>
            )}
          </dl>

          {Array.isArray(link.refunds) && link.refunds.length > 0 && (
            <div className="grow-callout warn grow-lpop-refunds">
              <ArrowCounterClockwise size={18} weight="fill" />
              <div>
                <strong>{link.refundStatus === "full" ? "העסקה זוכתה במלואה" : `זוכה חלקית — ${fmtMoney(link.refundedTotal)} מתוך ${fmtMoney(link.sum)}`}</strong>
                <ul className="grow-lpop-refund-list">
                  {link.refunds.map((r, i) => (
                    <li key={`${r.at || i}`}>
                      {fmtMoney(r.sum)}
                      {r.at ? ` · ${formatDateTime(r.at)}` : ""}
                      {r.byName ? ` · ${r.byName}` : ""}
                      {r.reason ? ` · ${r.reason}` : ""}
                    </li>
                  ))}
                </ul>
                <span className="grow-note">Grow אינה מפיקה חשבונית זיכוי אוטומטית.</span>
              </div>
            </div>
          )}

          {link.invoice?.number && (
            <div className="grow-callout">
              <Receipt size={18} weight="fill" />
              <span>
                קבלה מס' {link.invoice.number}
                {link.invoice.url && (
                  <> · <a href={link.invoice.url} target="_blank" rel="noreferrer" className="grow-link-external">פתיחה <ArrowSquareOut size={12} /></a></>
                )}
              </span>
            </div>
          )}

          {link.approveStatus === "failed" && isAdmin && (
            <p className="grow-note">אישור הקבלה ל-Grow נכשל — ייתכן ש-Grow ישלחו את העדכון שוב. אין צורך בפעולה.</p>
          )}

          {confirmCancel && (
            <div className="grow-callout warn">
              <Prohibit size={18} weight="fill" />
              <span>לבטל את דרישת התשלום? המטופל לא יוכל לשלם דרך הקישור הזה.</span>
            </div>
          )}
        </div>

        <div className="grow-modal-footer">
          {confirmCancel ? (
            <div className="grow-footer-actions">
              <button type="button" className="grow-btn secondary" onClick={() => setConfirmCancel(false)} disabled={busy}>חזרה</button>
              <button type="button" className="grow-btn danger" onClick={onCancel} disabled={busy}>
                {cancelling ? <LoaderLoop size="xs" /> : <Prohibit size={16} />} ביטול דרישת התשלום
              </button>
            </div>
          ) : (
            <div className="grow-footer-actions grow-lpop-actions">
              {link.url && active && (
                <button type="button" className="grow-btn secondary" onClick={onCopy} disabled={busy}>
                  <Copy size={16} /> העתקת קישור
                </button>
              )}
              {active && allowResend && (
                <button type="button" className="grow-btn secondary" onClick={onResend} disabled={busy}>
                  {resending ? <LoaderLoop size="xs" /> : <PaperPlaneTilt size={16} />} {link.sentAt ? "שליחה מחדש" : "שליחה למטופל"}
                </button>
              )}
              {active && (
                <button type="button" className="grow-btn ghost" onClick={onRefresh} disabled={busy} title="בדיקה מול Grow">
                  {refreshing ? <LoaderLoop size="xs" /> : <ArrowsClockwise size={16} />} עדכון סטטוס
                </button>
              )}
              {active && (
                <button type="button" className="grow-btn ghost danger-text" onClick={() => setConfirmCancel(true)} disabled={busy}>
                  <Prohibit size={16} /> ביטול
                </button>
              )}
              {!active && (
                <button type="button" className="grow-btn secondary" onClick={onClose}>סגירה</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
